import { FC, useState, useEffect } from 'react';
import { Button, Icon, Modal } from '@scale/scaleui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useStellarTasks } from '../../../../hooks/stellarGenAITask/useStellarGenAITasks';
import {
  GetStellarGenAITasksByProjectParams,
  GetStellarGenAITasksByProjectQuery,
  StellarGenAITaskStatus,
} from '@scale/shared/types/stellarGenAITask';
import { ChatTaskReadOnly } from '../../tasks/ThreadedInteraction/ChatTaskReadOnly';

interface StellarTasksButtonProps {
  projectId: string;
}

/**
 * StellarTasksModal opens a modal populated with read-only ChatTasks for the given project.
 */
export const StellarTasksModal: FC<StellarTasksButtonProps> = ({ projectId }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { data: stellarTasks, executeGet: getStellarTasks } = useStellarTasks();
  const [stellarTasksIndex, setStellarTasksIndex] = useState<number>(0); // Current task on display

  // Get Stellar tasks for this project.
  useEffect(() => {
    const params: GetStellarGenAITasksByProjectParams = {
      projectId,
    };
    const query: GetStellarGenAITasksByProjectQuery = {
      status: StellarGenAITaskStatus.Confirmed,
      limit: 10, // Randomized set of a few samples
    };
    getStellarTasks(params, query);
  }, [getStellarTasks, projectId]);

  // Go to previous task
  const handleOnBackward = () => {
    if (stellarTasksIndex > 0) {
      setStellarTasksIndex(stellarTasksIndex - 1);
    }
  };

  // Go to next task
  const handleOnForward = () => {
    if (stellarTasksIndex < stellarTasks.length - 1) {
      setStellarTasksIndex(stellarTasksIndex + 1);
    }
  };

  const modalTitle = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div>
        Stellar example ({stellarTasksIndex + 1} of {stellarTasks.length})
      </div>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'space-between' }}
      >
        <Icon onClick={handleOnBackward} disabled={stellarTasksIndex === 0}>
          <FontAwesomeIcon icon="angle-left" />
        </Icon>

        <Icon onClick={handleOnForward} disabled={stellarTasksIndex === stellarTasks.length - 1}>
          <FontAwesomeIcon icon="angle-right" />
        </Icon>
      </div>
    </div>
  );

  // Only render button if there's Stellar tasks for this project.
  if (stellarTasks.length > 0) {
    return (
      <div className="scaleui scale-theme scale-tailwind">
        <Modal
          open={isOpen}
          onClose={() => setIsOpen(false)}
          className="scaleui outlier-theme"
          title={modalTitle}
          innerClassName="w-full h-full"
        >
          <div className="scaleui scale-theme scale-tailwind">
            {stellarTasks[stellarTasksIndex].feedbackString}
            <ChatTaskReadOnly projectId={projectId} taskId={stellarTasks[stellarTasksIndex].task} />
          </div>
        </Modal>

        <Button
          variant="secondary"
          onClick={() => setIsOpen(true)}
          style={{ display: 'flex', gap: 10, justifyContent: 'space-between' }}
        >
          <FontAwesomeIcon icon="tasks" />
          Stellar examples
        </Button>
      </div>
    );
  } else {
    return null;
  }
};
